import type { AppRole, AppState, View } from '../types';
import { ProtoLightningMark } from './primitives';
import { TEAMS } from '../mock/data';
import { OFFSEASON } from '../config';

// Title sponsor logo for the "Presented by" header — theme-aware. The blue Wix
// artwork reads on the dark theme; the dedicated white-background SVG (vector,
// scales cleanly in <img>) reads on the light theme. Swapped via CSS by the
// [data-theme] attribute (see .theme-dark-only / .theme-light-only).
const MANIFOLD_LOGO_ON_DARK = 'https://static.wixstatic.com/media/6f41a3_10d8920a95d842858ab973ec4bc30072~mv2.png/v1/fill/w_414,h_133,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/manifold-logo-blue-transparent_edited_pn.png';
const MANIFOLD_LOGO_ON_LIGHT = '/assets/ManifoldTech.svg';

interface NavItem { id: View; label: string }
interface NavGroup { group: string; items: NavItem[] }

// Counts shown next to schedule entries are derived from live state by
// the sidebar component (see deriveCounts below) — we deliberately do NOT
// hardcode them here. NAV_BY_ROLE captures the structure (which views
// exist in which group), not the data.
export const NAV_BY_ROLE: Record<AppRole, NavGroup[]> = {
  public: [
    { group: 'SCHEDULES', items: [
      { id: 'sponsors', label: 'Sponsors' },
      { id: 'sched-3v3', label: '3v3' },
      { id: 'sched-1v1', label: '1v1' },
      { id: 'sched-arctic', label: 'ARCTIC' },
      { id: 'sched-ec', label: 'Engineer' },
      { id: 'sched-practice', label: 'Practice' },
    ]},
    { group: 'RESULTS', items: [
      { id: 'recap', label: 'Recap' },
      { id: 'bracket', label: 'Brackets' },
      { id: 'standings', label: 'Quals Standings' },
      { id: 'results', label: 'Results' },
      { id: 'teams', label: 'Teams' },
      { id: 'pictures', label: 'Pictures' },
    ]},
  ],
  // Volunteers: same nav as staff EXCEPT no Warnings entry. Server-side
  // RLS doesn't grant volunteer the warnings tier; the UI hides the entry
  // so volunteers never have a path to that screen in the first place.
  volunteer: [
    { group: 'LIVE', items: [
      { id: 'control', label: 'Match Control' },
      { id: 'inspection', label: 'Inspection' },
      { id: 'preinspection', label: 'Pre-Inspection' },
    ]},
    { group: 'SCHEDULES', items: [
      { id: 'sponsors', label: 'Sponsors' },
      { id: 'sched-3v3', label: '3v3' },
      { id: 'sched-1v1', label: '1v1' },
      { id: 'sched-arctic', label: 'ARCTIC' },
      { id: 'sched-ec', label: 'Engineer' },
      { id: 'sched-practice', label: 'Practice' },
    ]},
    { group: 'DATA', items: [
      { id: 'recap', label: 'Recap' },
      { id: 'bracket', label: 'Brackets' },
      { id: 'standings', label: 'Quals Standings' },
      { id: 'results', label: 'Results' },
      { id: 'teams', label: 'Teams' },
      { id: 'pictures', label: 'Pictures' },
    ]},
  ],
  staff: [
    { group: 'LIVE', items: [
      { id: 'control', label: 'Match Control' },
      { id: 'inspection', label: 'Inspection' },
      { id: 'preinspection', label: 'Pre-Inspection' },
      { id: 'warnings', label: 'Warnings' },
    ]},
    { group: 'SCHEDULES', items: [
      { id: 'sponsors', label: 'Sponsors' },
      { id: 'sched-3v3', label: '3v3' },
      { id: 'sched-1v1', label: '1v1' },
      { id: 'sched-arctic', label: 'ARCTIC' },
      { id: 'sched-ec', label: 'Engineer' },
      { id: 'sched-practice', label: 'Practice' },
    ]},
    { group: 'DATA', items: [
      { id: 'recap', label: 'Recap' },
      { id: 'bracket', label: 'Brackets' },
      { id: 'standings', label: 'Quals Standings' },
      { id: 'results', label: 'Results' },
      { id: 'teams', label: 'Teams' },
      { id: 'pictures', label: 'Pictures' },
    ]},
  ],
  ops: [
    { group: 'LIVE', items: [
      { id: 'control', label: 'Match Control' },
      { id: 'inspection', label: 'Inspection' },
      { id: 'preinspection', label: 'Pre-Inspection' },
      { id: 'warnings', label: 'Warnings' },
    ]},
    { group: 'SCHEDULES', items: [
      { id: 'sponsors', label: 'Sponsors' },
      { id: 'sched-3v3', label: '3v3' },
      { id: 'sched-1v1', label: '1v1' },
      { id: 'sched-arctic', label: 'ARCTIC' },
      { id: 'sched-ec', label: 'Engineer' },
      { id: 'sched-practice', label: 'Practice' },
    ]},
    { group: 'DATA', items: [
      { id: 'recap', label: 'Recap' },
      { id: 'bracket', label: 'Brackets' },
      { id: 'standings', label: 'Quals Standings' },
      { id: 'results', label: 'Results' },
      { id: 'teams', label: 'Teams' },
      { id: 'pictures', label: 'Pictures' },
    ]},
    { group: 'OPERATIONS', items: [
      { id: 'checkin', label: 'Check-In' },
      { id: 'fieldcal', label: 'Field Calibration' },
      { id: 'referee', label: 'Referee System' },
      { id: 'admin', label: "Captain's Meeting" },
      { id: 'access', label: 'Access Control' },
    ]},
  ],
};

// Derives the row counts shown next to nav entries.
//   3v3 / 1v1: live + upcoming matches (matches the topbar's "X UPCOMING").
//   ARCTIC:    total scheduled entries (no per-entry completion concept).
//   EC:        runs that haven't completed yet.
//   Results:   completed matches across all events (3v3 + 1v1 + EC).
//   Teams:     total registered teams.
// Returns Partial<Record<View, number>> so the lookup is a simple .get
// and missing entries naturally render no count.
const COMPLETED_LIKE: Array<'completed' | 'forfeit_v' | 'forfeit_iv'> = [
  'completed', 'forfeit_v', 'forfeit_iv',
];
function deriveCounts(state: AppState | undefined): Partial<Record<View, number>> {
  if (!state) return {};
  const isLiveOrUpcoming = (m: { redS: string; blueS: string; live?: boolean; onDeck?: boolean }) =>
    m.live || m.onDeck
    || !(COMPLETED_LIKE.includes(m.redS as typeof COMPLETED_LIKE[number])
      && COMPLETED_LIKE.includes(m.blueS as typeof COMPLETED_LIKE[number]));
  const isCompleted = (m: { redS: string; blueS: string }) =>
    COMPLETED_LIKE.includes(m.redS as typeof COMPLETED_LIKE[number])
    && COMPLETED_LIKE.includes(m.blueS as typeof COMPLETED_LIKE[number]);
  const completed3v3 = state.matches3v3.filter(isCompleted).length;
  const completed1v1 = state.matches1v1.filter(isCompleted).length;
  const completedEC  = state.ec.filter((e) => e.completed).length;
  return {
    'sched-3v3':    state.matches3v3.filter(isLiveOrUpcoming).length,
    'sched-1v1':    state.matches1v1.filter(isLiveOrUpcoming).length,
    'sched-arctic': state.arctic.length,
    'sched-ec':     state.ec.filter((e) => !e.completed).length,
    'sched-practice': state.practiceMatches.filter((m) => m.status !== 'completed').length,
    results:        completed3v3 + completed1v1 + completedEC,
    teams:          TEAMS.length,
  };
}

export const ROLE_META: Record<AppRole, { label: string; pill: string }> = {
  public:    { label: 'PUBLIC',     pill: 'No Auth' },
  volunteer: { label: 'VOLUNTEER',  pill: 'Google SSO · No Warnings' },
  staff:     { label: 'STAFF',      pill: 'Google SSO' },
  ops:       { label: 'OPERATIONS', pill: 'Google SSO + 2FA' },
};

export function ProtoSidebar({
  role, active, onNavigate, user, open, onClose, state, isSuperuser = false, isAuthenticated = false, onEditName,
}: {
  role: AppRole;
  active: View;
  onNavigate: (id: View) => void;
  user: { name: string; initials: string };
  open: boolean;
  onClose: () => void;
  /** When provided, schedule entries show derived live-or-upcoming counts. */
  state?: AppState;
  /** Access Control is superuser-only — drop the nav entry otherwise. */
  isSuperuser?: boolean;
  /** Any signed-in user (incl. roleless) → show the Fantasy entry. */
  isAuthenticated?: boolean;
  /** Click the user footer to edit the fantasy display handle. */
  onEditName?: () => void;
}) {
  // 'access' is superuser-only; 'fantasy' is shown to any signed-in user. Both
  // are gated here rather than baked into NAV_BY_ROLE.
  const groups = (NAV_BY_ROLE[role] ?? [])
    // Offseason: the public archive drops the live SCHEDULES group (the live
    // schedules + sponsor page). Signed-in roles keep it for testing / Season Two.
    .filter((g) => !(OFFSEASON && role === 'public' && g.group === 'SCHEDULES'))
    .map((g) => {
      let items = g.items.filter((it) => it.id !== 'access' || isSuperuser);
      // Inject Fantasy next to Bracket (the results/data group is named
      // 'RESULTS' for public, 'DATA' for vol+, so key off the Bracket item).
      // Shown to everyone incl. anon — logged-out users get a sign-in teaser so
      // they discover the feature before authenticating.
      if (items.some((it) => it.id === 'bracket')) {
        items = [...items, { id: 'fantasy' as View, label: 'Fantasy' }];
      }
      return { ...g, items };
    })
    .filter((g) => g.items.length > 0);
  const counts = deriveCounts(state);
  // After picking a nav item: on mobile, close the slide-in drawer so the
  // user can see the page they navigated to. On desktop, the sidebar is
  // a persistent rail — leave it open. Match the same breakpoint as the
  // tokens.css media query (≤760px = mobile).
  const handleNavigate = (id: View) => {
    onNavigate(id);
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches) {
      onClose();
    }
  };
  return (
    <>
      <div className={`app-sidebar-scrim ${open ? 'open' : ''}`} onClick={onClose} />
      <aside
        className={`app-sidebar col gap-4 shrink-0 bg-1 ${open ? 'open' : ''}`}
        style={{ width: 210, borderRight: '1px solid var(--line)', padding: '14px 12px' }}
      >
        <div className="col gap-3 border-b" style={{ padding: '0 8px 12px' }}>
          <div className="row gap-2">
            <ProtoLightningMark size={30} />
            <div>
              <div className="display txt-26 lh-1 tracking-1">LIGHTNING</div>
              <div className="mono txt-11 text-2 tracking-4" style={{ marginTop: 3 }}>ARC Season One</div>
            </div>
          </div>
          <div className="col gap-1" style={{ alignItems: 'flex-start' }}>
            <div className="mono txt-9 text-3 tracking-4">PRESENTED BY</div>
            <img
              src={MANIFOLD_LOGO_ON_DARK}
              alt="Manifold Tech"
              loading="lazy"
              referrerPolicy="no-referrer"
              className="theme-dark-only"
              style={{ height: 26, width: 'auto', maxWidth: 150, objectFit: 'contain' }}
            />
            <img
              src={MANIFOLD_LOGO_ON_LIGHT}
              alt="Manifold Tech"
              loading="lazy"
              referrerPolicy="no-referrer"
              className="theme-light-only"
              style={{ height: 26, width: 'auto', maxWidth: 150, objectFit: 'contain' }}
            />
          </div>
        </div>
        <div className="col gap-5 grow overflow-auto">
          {groups.map((g) => (
            <div key={g.group}>
              <div className="section-label mb-2 px-2">{g.group}</div>
              <div className="col" style={{ gap: 1 }}>
                {g.items.map((it) => {
                  const count = counts[it.id];
                  return (
                    <div
                      key={it.id}
                      onClick={() => handleNavigate(it.id)}
                      className={`nav-item between ${it.id === active ? 'active' : ''}`}
                    >
                      <span className="row gap-3">
                        <span className="dot" />{it.label}
                      </span>
                      {count !== undefined && (
                        <span className="mono txt-10 text-3">{count}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="col">
          <div className="row gap-2 border-t" style={{ padding: '10px 8px 6px', cursor: isAuthenticated && onEditName ? 'pointer' : 'default' }}
            onClick={isAuthenticated && onEditName ? onEditName : undefined}
            title={isAuthenticated && onEditName ? 'Edit your fantasy display name' : undefined}>
            <div className="grid-place fw-bold txt-11 bg-3 r-full" style={{ width: 28, height: 28 }}>{user.initials}</div>
            <div className="grow min-w-0 lh-snug">
              <div className="text-0 fw-semi txt-12 truncate">{user.name}{isAuthenticated && onEditName && <span className="text-3"> ✎</span>}</div>
              <div className="mono txt-9 uppercase tracking-4 text-2">{ROLE_META[role].label}</div>
            </div>
          </div>
          <div className="row gap-4" style={{ padding: '0 8px 2px' }}>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="mono txt-9 tracking-2 footer-link">Privacy</a>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="mono txt-9 tracking-2 footer-link">Terms</a>
          </div>
        </div>
      </aside>
    </>
  );
}
